/**
 * Writing module data transformation utilities.
 *
 * Transforms backend SimulationData structure to writing module format.
 * Extracts essay prompts, images, and fill-in-the-blank questions.
 *
 * Essay tasks are surfaced as a list (one per essay question across all
 * sections), so any level that ships multiple essay sections (e.g. B1 Goethe
 * with three sections) renders each as its own task — the count follows
 * whatever the backend returns, with no level-specific branching.
 *
 * @module writingDataTransform
 */

import type {
  AdaptiveQuestionState,
  SimulationData,
  SimulationSection,
  SimulationQuestion,
  SimulationContext,
} from '@/types/exam-simulation';

/**
 * Text segment for fill-in-blank form rendering
 */
export interface TextSegment {
  type: 'text' | 'blank';
  content?: string;
  blankId?: string;
}

/**
 * Blank field definition
 */
export interface BlankField {
  id: string;
  expectedAnswer?: string;
  placeholder?: string;
}

/**
 * Fill-in-blank form data structure
 */
export interface FillBlankFormData {
  /** Text segments for inline rendering */
  segments: TextSegment[];
  /** Blank field definitions */
  blanks: BlankField[];
  /** Prompt/instructions for the form */
  prompt: string;
  /** Original question ID for submission */
  questionId?: string;
  /** Section number for submission */
  section: number;
  /** Adaptive state for this question. */
  adaptiveState?: AdaptiveQuestionState;
}

/**
 * Single essay task extracted from a backend essay question.
 */
export interface EssayTaskData {
  /** Essay prompt text (context content or question text). */
  prompt: string;
  /** Task type label (question instruction / section title). */
  taskType: string;
  /** Optional image URL from context. */
  imageUrl?: string;
  /** Backing question ID for answer submission. */
  questionId: string;
  /** Section number for answer submission. */
  section: number;
  /** Adaptive state for this essay question. */
  adaptiveState: AdaptiveQuestionState;
}

/**
 * Writing module data structure
 */
export interface WritingModuleData {
  /** Essay tasks in section order (one per essay question across all sections). */
  essayTasks: EssayTaskData[];
  /** Fill-in-blank form data (for WritingFormPanel) */
  fillBlankForms: FillBlankFormData[];
  /** Section number of the displayed fill-in-blank task (1 when none). */
  fillBlankSection: number;
  /** Flags indicating which question types are available */
  hasFillBlank: boolean;
  hasEssay: boolean;
}

/**
 * Parse form text into segments and blanks for WritingFormPanel
 *
 * @param formText - The form text with blank markers
 * @param questionId - The question ID for blank naming
 * @returns Segments and blanks for WritingFormPanel
 */
function parseFormTextToSegments(
  formText: string,
  questionId: string,
): { segments: TextSegment[]; blanks: BlankField[] } {
  const segments: TextSegment[] = [];
  const blanks: BlankField[] = [];

  // Try to find {BLANK:id} markers first
  const blankPattern = /\{BLANK:([^}]+)\}/g;
  let lastIndex = 0;
  let match;
  let blankIndex = 0;

  while ((match = blankPattern.exec(formText)) !== null) {
    // Add text before the blank
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: formText.substring(lastIndex, match.index),
      });
    }

    const blankId = match[1] || `blank-${questionId}-${blankIndex}`;
    segments.push({ type: 'blank', blankId });
    blanks.push({ id: blankId, placeholder: '...' });
    blankIndex++;
    lastIndex = blankPattern.lastIndex;
  }

  // Add remaining text after last blank
  if (lastIndex < formText.length && blankIndex > 0) {
    segments.push({
      type: 'text',
      content: formText.substring(lastIndex),
    });
  }

  // If no {BLANK:id} markers found, try "_____" patterns (3+ underscores)
  if (blankIndex === 0) {
    const underscorePattern = /_{3,}/g;
    lastIndex = 0;

    while ((match = underscorePattern.exec(formText)) !== null) {
      // Add text before the blank
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: formText.substring(lastIndex, match.index),
        });
      }

      const blankId = `blank-${questionId}-${blankIndex}`;
      segments.push({ type: 'blank', blankId });
      blanks.push({ id: blankId, placeholder: '...' });
      blankIndex++;
      lastIndex = underscorePattern.lastIndex;
    }

    // Add remaining text after last blank
    if (lastIndex < formText.length && blankIndex > 0) {
      segments.push({
        type: 'text',
        content: formText.substring(lastIndex),
      });
    }
  }

  // If no blanks found at all, treat the whole text as content
  if (segments.length === 0) {
    segments.push({ type: 'text', content: formText });
  }

  return { segments, blanks };
}

/**
 * Extract fill-in-the-blank questions from backend data
 *
 * Backend structure:
 * - questionGroup.context.content: The scenario/context text (shown in left panel)
 * - question.text: The actual form with blanks (the form to fill in)
 *
 * @param questionGroups - Question groups from backend (to access context)
 * @param sectionNum - Section number
 * @returns Object with fillBlankForms for WritingFormPanel
 */
function extractFillBlankData(
  questionGroups: Array<{
    context?: SimulationContext | null;
    questions: SimulationQuestion[];
  }>,
  sectionNum: number,
): { fillBlankForms: FillBlankFormData[] } {
  const fillBlankForms: FillBlankFormData[] = [];

  questionGroups.forEach((group) => {
    // Get context from question group (this is the scenario text for the left panel)
    const contextText = group.context?.content || '';

    group.questions.forEach((question) => {
      // Check if this is a fill-in-the-blank question
      const isFillBlank =
        question.type === 'fill-in-blank' ||
        question.type === 'fill_in_blank' ||
        question.type === 'fill_in_blank_question';

      if (!isFillBlank) {
        return;
      }

      // Backend structure:
      // - group.context.content = scenario/context (the story/situation) - shown in left panel
      // - question.text = the actual form with blanks to fill in - shown in right panel
      const formText = question.text || '';

      // Parse form text into segments and blanks for WritingFormPanel
      const { segments, blanks } = parseFormTextToSegments(formText, question.id);

      // Create form data for WritingFormPanel
      // Use context.content as the prompt (scenario text for left panel)
      fillBlankForms.push({
        segments,
        blanks,
        prompt: contextText || 'Fill in the blanks with appropriate words.',
        questionId: question.id,
        section: sectionNum,
        adaptiveState: question.adaptiveState ?? 'new',
      });
    });
  });

  return { fillBlankForms };
}

function isEssayQuestion(question: SimulationQuestion): boolean {
  return (
    question.type === 'text-long' ||
    question.type === 'essay' ||
    question.type === 'writing'
  );
}

/**
 * Collect every essay question across all sections in section order.
 *
 * Each essay question becomes its own task — the count follows whatever the
 * backend ships for the level, with no level-specific filtering.
 */
function extractEssayTasks(sections: SimulationSection[]): EssayTaskData[] {
  const tasks: EssayTaskData[] = [];

  for (const section of sections) {
    for (const group of section.questionGroups) {
      const context = group.context;
      for (const question of group.questions) {
        if (!isEssayQuestion(question)) {
          continue;
        }

        const prompt =
          context?.content ||
          question.text ||
          section.title ||
          'Write a response to the following prompt.';

        const taskType =
          question.text || section.title || context?.name || 'Writing Task';

        const imageUrl = context?.binaryContent
          ? `data:${context.mimeType || 'image/png'};base64,${context.binaryContent}`
          : context?.mediaUrl || undefined;

        tasks.push({
          prompt,
          taskType,
          imageUrl,
          questionId: question.id,
          section: section.section,
          adaptiveState: question.adaptiveState ?? 'new',
        });
      }
    }
  }

  return tasks;
}

/**
 * Transform backend simulation data to writing module format
 *
 * Dynamically detects question types across all sections and extracts
 * fill-in-blank and essay questions regardless of section numbers.
 *
 * @param data - Backend simulation data
 * @returns Transformed writing module data
 */
export function transformWritingModuleData(data: SimulationData | null): WritingModuleData {
  if (!data?.sections?.length) {
    return {
      essayTasks: [],
      fillBlankForms: [],
      fillBlankSection: 1,
      hasFillBlank: false,
      hasEssay: false,
    };
  }

  const essayTasks = extractEssayTasks(data.sections);

  // Extract fill-in-blank questions from any section containing them
  const fillBlankSection = data.sections.find((s) =>
    s.questionGroups.some((group) =>
      group.questions.some(
        (q) =>
          q.type === 'fill-in-blank' ||
          q.type === 'fill_in_blank' ||
          q.type === 'fill_in_blank_question',
      ),
    ),
  );

  const { fillBlankForms } = fillBlankSection
    ? extractFillBlankData(fillBlankSection.questionGroups, fillBlankSection.section)
    : { fillBlankForms: [] };

  return {
    essayTasks,
    fillBlankForms,
    fillBlankSection: fillBlankSection?.section ?? 1,
    hasFillBlank: fillBlankForms.length > 0,
    hasEssay: essayTasks.length > 0,
  };
}
